<script setup lang="ts">
  import { computed } from 'vue';
  import { useRouter } from 'vue-router';
  import { useSidebarStore } from '../../store/sidebar.store';
  import {
    sidebarItemCollapsedClass,
    sidebarItemContainerClass,
    sidebarItemSelectedClass,
    subItemSelectedVariants,
    subItemVariants,
  } from './SidebarSubItem.css';
  import type { SidebarSubItemProps } from './SidebarSubItem.types';

  const props = defineProps<SidebarSubItemProps>();

  const sidebarStore = useSidebarStore();

  const router = useRouter();

  const resolvedHref = computed(() =>
    props.to ? router.resolve(props.to).href : undefined,
  );

  const isSelected = computed(() => {
    if (props.to) {
      const resolved = router.resolve(props.to);
      const currentPath = router.currentRoute.value.path;
      return (
        currentPath === resolved.path ||
        currentPath.startsWith(`${resolved.path}/`)
      );
    }
    return false;
  });

  const sidebarItemClasses = computed(() => [
    sidebarItemContainerClass,
    subItemVariants[sidebarStore.variant],
    {
      [sidebarItemSelectedClass]: isSelected.value,
      [subItemSelectedVariants[sidebarStore.variant]]: isSelected.value,
      [sidebarItemCollapsedClass]: sidebarStore.collapsed,
    },
  ]);

  const onNavigate = () => {
    if (props.to) {
      router.push(props.to);
    }
    if (props.onClick) {
      props.onClick();
    }
  };

  const onMiddleClick = () => {
    if (props.onMiddleClick) {
      props.onMiddleClick();
    }
  };
</script>

<template>
  <a
    v-if="to"
    :href="resolvedHref"
    :data-testid="testId"
    :class="sidebarItemClasses"
    :aria-current="isSelected ? 'page' : undefined"
    @click.stop.prevent="onNavigate"
    @auxclick="onMiddleClick"
  >
    <span>{{ label }}</span>
  </a>
  <div
    v-else
    :data-testid="testId"
    :class="sidebarItemClasses"
    role="link"
    tabindex="0"
    @click.stop.prevent="onNavigate"
    @keydown.enter.prevent="onNavigate"
  >
    <span>{{ label }}</span>
  </div>
</template>
